import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

/** Radix reserves the empty string for «clear», so «no choice» travels under this value. */
const NONE = '__none__';

export type Choice = { readonly value: string; readonly label: string };

/**
 * The console's `Select` for a short list of `{ value, label }` choices, one of which may be the
 * empty value («Nessuna nota», «Scegli un veicolo…»). Same look and keyboard model as the other
 * pages' selects; `id` goes to the trigger so a `<label htmlFor>` names it.
 */
export function ChoiceSelect({
  id,
  value,
  choices,
  onChange,
  invalid,
  disabled,
  itemClassName,
  triggerClassName,
}: {
  readonly id: string;
  readonly value: string;
  readonly choices: readonly Choice[];
  readonly onChange: (value: string) => void;
  readonly invalid?: boolean;
  readonly disabled?: boolean;
  readonly itemClassName?: string;
  /** Extra classes for the trigger, e.g. `min-h-11` beside 44 px fields. */
  readonly triggerClassName?: string;
}) {
  return (
    <Select
      value={value === '' ? NONE : value}
      onValueChange={(next) => onChange(next === NONE ? '' : next)}
      {...(disabled === undefined ? {} : { disabled })}
    >
      <SelectTrigger
        id={id}
        className={cn('w-full', triggerClassName)}
        aria-invalid={invalid === true}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {choices.map((choice) => (
          <SelectItem
            key={choice.value}
            value={choice.value === '' ? NONE : choice.value}
            className={itemClassName}
          >
            {choice.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
